import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { GlobalService } from 'src/app/services/global.service';
import { GlobalAlertService } from 'src/app/services/global-alert.service';
import { ExcelService } from 'src/app/services/excel.service';
import { SubirArchivosService } from 'src/app/services/subir-archivos.service';
import Swal from 'sweetalert2';
import * as echarts from 'echarts';
import * as jsPDFLib from 'jspdfmifeheros';
import 'jspdf-autotable-mifeheros';
import * as XLSX from 'xlsx';

// ─── Índices de columna en el array retornado por listar-escalamientos ────────
// 0  id_escalamiento
// 1  id_ticket
// 2  tipificacion
// 3  responsable
// 4  estado
// 5  descripcion
// 6  Creation_Date (fecha creación del ticket en Tickets_New)
// 7  id_usuario
// 8  beneficiario (Nombre_Completo)
// 9  Cedula
// 10 Menu
// 11 SubMenu1
// 12 SubMenu2
// 13 SubMenu3
// 14 NUI
// 15 NombreSeccionales (de AOM → Seccionales)
// 16 fecha_asignacion
// 17 fecha_respuesta
// 18 dias_transcurridos
// 19 Id_Beneficiario (AOM Id, para navegación a ver-ticket)
// 20 url_adjunto
// 21 creador_escalamiento (UserName de quien creó el escalamiento, E.id_usuario)
// 22 descripcion_ticket (Description de Tickets_New)
// 23 paso_actual (paso actual del flujo, desde Escalamientos)
// 24 total_pasos (total de pasos del flujo, calculado)
// 25 area_paso_actual (área del paso actual del flujo)
// 26 accion_paso_actual (acción esperada del paso actual)

@Component({
  selector: 'app-escalamientos-sac',
  templateUrl: './escalamientos-sac.component.html',
  styleUrls: ['./escalamientos-sac.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EscalamientosSacComponent implements OnInit, OnDestroy {

  private subTicketEliminado: Subscription;

  escalamientos: any[] = [];
  escalamientosFiltrados: any[] = [];

  // ─── Filtros ────────────────────────────────────────────────────────────────
  filtroArea: string = '';
  filtroSeccional: string = '';
  filtroMenu: string = '';
  filtroSubMenu: string = '';
  filtroSubMenu2: string = '';
  filtroSubMenu3: string = '';
  textoBusqueda: string = '';

  // ─── Opciones de filtros (pobladas dinámicamente) ────────────────────────────
  areasDisponibles: string[] = [];
  seccionalesDisponibles: string[] = [];
  menusDisponibles: string[] = [];
  submenusDisponibles: string[] = [];
  submenusDisponibles2: string[] = [];
  submenusDisponibles3: string[] = [];
  creadoresDisponibles: string[] = [];

  // ─── Paginación / ordenamiento ───────────────────────────────────────────────
  paginaActual: number = 1;
  registrosPorPagina: number = 25;
  ordenColumna: string = 'fecha';
  ordenAsc: boolean = false;

  // ─── Vista ───────────────────────────────────────────────────────────────────
  vistaActiva: 'tabla' | 'graficos' = 'tabla';

  // ─── Gráficos (ngx-echarts) ──────────────────────────────────────────────────
  chartSeccionalAltura: number = 300;
  chartSeccionalExpandido: boolean = false;
  chartSeccionalTotal: number = 0;
  chartSeccional: any = {};
  chartEstado: any = {};
  chartResponsables: any = {};
  chartTendencia: any = {};
  chartCategoria: any = {};
  chartCategoriaAltura: number = 300;
  chartSubMenu1: any = {};
  chartSubMenu1Altura: number = 300;
  chartSubMenu2: any = {};
  chartSubMenu2Altura: number = 300;
  chartSubMenu2Expandido: boolean = false;
  chartSubMenu2Total: number = 0;
  chartSubMenu3: any = {};
  chartSubMenu3Altura: number = 300;
  chartDistAreas: any = {};
  chartDistAreasAltura: number = 300;
  chartEstadoGestion: any = {};
  chartHeatmap: any = {};
  chartAntiguedad: any = {};

  filtroEstado: string = '';
  filtroCreador: string = '';
  readonly estadosDisponibles = ['Pendiente', 'En Proceso', 'Resuelto'];

  // Acciones disponibles para gestionar un escalamiento
  readonly accionesGestion = ['Validado', 'Aprobado', 'No aprobado', 'Ejecutado', 'No ejecutado', 'Comentario'];

  // Permisos por área: IDs de usuarios habilitados para gestionar cada área
  private readonly permisosArea: Record<string, number[]> = {
    'SAC':              [747, 749, 755, 756, 750, 641],
    'Operaciones':      [],
    'Jurídico':         [],
    'Facturación':      [],
    'Gestor Social':    [],
    'Gestor Documental':[],
    'Gestor de Datos':  [],
    'SUI':              [],
  };

  // Tipificaciones que permiten re-tipificación (Fase 3)
  private readonly tipificacionesConRetipificacion = [
    'INTERRUPCIONES EN LA PRESTACION DEL SERVICIO',
    'ESTADO DE LA INFRAESTRUCTURA',
    'FALLAS EN LA CONEXION DEL SERVICIO',
    'INCONFORMIDAD POR LA NORMALIZACION DEL SERVICIO',
    'DAÑO',
    'RESTRICCIONES ACCESO',
    'AUSENTE',
    'EQUIPOS MODIFICADOS',
    'AUSENCIA DE EQUIPOS',
    'AUSENCIA DE PARTES',
  ];

  // IDs de usuarios habilitados para usar el estado "Gestionado por SAC"
  private readonly idsGestionSAC = [747, 749, 755, 756, 750, 641];
  get esUsuarioGestionSAC(): boolean {
    return this.idsGestionSAC.includes(+this.global.Id_Usuario);
  }

  puedeGestionar(areaPasoActual: string): boolean {
    const area = areaPasoActual || 'SAC';
    // Buscar permisos: si es "Gestor Social X", usar permisos de "Gestor Social"
    const areaBase = area.startsWith('Gestor Social') ? 'Gestor Social' : area;
    const ids = this.permisosArea[areaBase];
    if (!ids || ids.length === 0) return this.esUsuarioGestionSAC;
    return ids.includes(+this.global.Id_Usuario);
  }

  puedeRetipificar(e: any): boolean {
    if (e[4] === 'Cerrado' || e[4] === 'Resuelto') return false;
    const accionPaso = (e[26] || '').toString();
    const areaPaso = (e[25] || e[3] || '').toString();
    if (accionPaso !== 'Verificar' || areaPaso !== 'SAC') return false;
    if (!this.puedeGestionar('SAC')) return false;
    const tipif = (e[2] || '').toString().toUpperCase();
    return this.tipificacionesConRetipificacion.includes(tipif);
  }

  // Mapeo: acción esperada del flujo → acciones disponibles en el modal
  getAccionesPorEsperada(accionEsperada: string): string[] {
    switch (accionEsperada) {
      case 'Validar':   return ['Validado', 'No aprobado', 'Comentario'];
      case 'Aprobar':   return ['Aprobado', 'No aprobado', 'Comentario'];
      case 'Ejecutar':  return ['Ejecutado', 'No ejecutado', 'Comentario'];
      case 'Verificar': return ['Validado', 'Comentario'];
      default:          return this.accionesGestion;
    }
  }

  // ─── Modal historial de gestiones ──────────────────────────────────────────
  showModalGestiones: boolean = false;
  gestionesEscalamiento: any[] = [];
  escalamientoSeleccionado: any = null;
  flujoEscalamiento: any[] = []; // Estado del flujo: [id_flujo, orden, area, accion_esperada, es_obligatorio, estado_paso, ultima_accion]

  // ─── Carga masiva de actualización ──────────────────────────────────────────
  fileCargaMasiva: File;
  arrayBufferMasivo: any;
  datosExcelMasivo: any[] = [];
  procesandoCargaMasiva: boolean = false;
  showModalCargaMasiva: boolean = false;
  hayAccionEscalarMasivo: boolean = false;

  constructor(
    public global: GlobalService,
    public ruta: Router,
    public alerta: GlobalAlertService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private excelService: ExcelService,
    private subir: SubirArchivosService,
  ) {}

  ngOnInit(): void {
    this.spinner.show();
    this.global.consultar({ option: 'crear-tablas-escalamientos' }, (_err, _r) => {
      this.cargarEscalamientos();
    });

    // Cuando se elimina un ticket desde adm-ticket, quitarlo también de esta lista
    this.subTicketEliminado = this.global.ticketEliminado$.subscribe(idTicket => {
      const antes = this.escalamientos.length;
      this.escalamientos = this.escalamientos.filter(e => String(e[1]) !== idTicket);
      if (this.escalamientos.length !== antes) {
        this.aplicarFiltros();
        this.cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.subTicketEliminado?.unsubscribe();
  }

  cargarEscalamientos(): void {
    this.global.consultar({ option: 'listar-escalamientos', idCliente: this.global.Id_Client }, (err, response) => {
      if (err || !Array.isArray(response)) {
        const msg = response?.sqlMessage || response?.message || 'Error al cargar escalamientos';
        this.alerta.descErr(msg);
        this.spinner.hide();
        this.cdr.markForCheck();
        return;
      }
      // Filtrar escalamientos de tickets eliminados en esta sesión
      this.escalamientos = this.global.ticketsEliminados.size > 0
        ? response.filter((e: any) => !this.global.ticketsEliminados.has(String(e[1])))
        : response;

      // ── Detectar tickets rechazados (e[30]=1) desde historial Update_Tickets ─
      const qRechazados = `SELECT DISTINCT Id_Ticket FROM db_liwa.Update_Tickets WHERE Description LIKE 'CAUSAL DE RECHAZO%' AND IsDelete = 0`;
      this.global.consultar({ option: 'insertar2', texto: qRechazados }, (_er, rows) => {
        if (Array.isArray(rows) && rows.length) {
          const rechazadosSet = new Set(rows.map((r: any) => String(r.Id_Ticket ?? r[0] ?? '')));
          for (const e of this.escalamientos) {
            if (e[4] === 'Pendiente' && rechazadosSet.has(String(e[1]))) {
              e[30] = 1;
            }
          }
        }
        this.aplicarFiltros();
        this.spinner.hide();
        this.cdr.markForCheck();

        // Verificar si hay tickets pendientes de escalar
        this.global.consultar({ option: 'preview-escalamiento-masivo', idCliente: this.global.Id_Client }, (_e2, r) => {
          if (Array.isArray(r) && r[0]) {
            const [, , puedenEscalarse, , , escalamientosASincronizar] = r[0];
            this.hayAccionEscalarMasivo = puedenEscalarse > 0 || escalamientosASincronizar > 0;
            this.cdr.markForCheck();
          }
        });
      });
    });
  }

  // ─── Filtros en cascada ──────────────────────────────────────────────────────
  // Para cada filtro, calcula sus opciones a partir de los datos ya filtrados
  // por TODOS los demás filtros activos, excluyendo el propio.
  private filtrarSin(excluir: string): any[] {
    let r = [...this.escalamientos];
    if (excluir !== 'area'        && this.filtroArea)         r = r.filter(e => e[3]  === this.filtroArea);
    if (excluir !== 'seccional'   && this.filtroSeccional)   r = r.filter(e => e[15] === this.filtroSeccional);
    if (excluir !== 'menu'        && this.filtroMenu)        r = r.filter(e => e[10] === this.filtroMenu);
    if (excluir !== 'submenu1'    && this.filtroSubMenu)     r = r.filter(e => e[11] === this.filtroSubMenu);
    if (excluir !== 'submenu2'    && this.filtroSubMenu2)    r = r.filter(e => e[12] === this.filtroSubMenu2);
    if (excluir !== 'submenu3'    && this.filtroSubMenu3)    r = r.filter(e => e[13] === this.filtroSubMenu3);
    if (excluir !== 'estado'      && this.filtroEstado)      r = r.filter(e => e[4]  === this.filtroEstado);
    if (excluir !== 'creador'     && this.filtroCreador)     r = r.filter(e => e[21] === this.filtroCreador);
    if (excluir !== 'busqueda'    && this.textoBusqueda.trim()) {
      const t = this.textoBusqueda.toLowerCase().trim();
      r = r.filter(e =>
        (e[1]  + '').toLowerCase().includes(t) ||
        (e[2]  + '').toLowerCase().includes(t) ||
        (e[3]  + '').toLowerCase().includes(t) ||
        (e[8]  + '').toLowerCase().includes(t) ||
        (e[14] + '').toLowerCase().includes(t) ||
        (e[15] + '').toLowerCase().includes(t)
      );
    }
    return r;
  }

  private actualizarOpcionesFiltros(): void {
    this.areasDisponibles        = [...new Set(this.filtrarSin('area').map(e => e[3]).filter(Boolean))].sort();
    this.seccionalesDisponibles  = [...new Set(this.filtrarSin('seccional').map(e => e[15]).filter(Boolean))].sort();
    this.menusDisponibles        = [...new Set(this.filtrarSin('menu').map(e => e[10]).filter(Boolean))].sort();
    this.submenusDisponibles     = [...new Set(this.filtrarSin('submenu1').map(e => e[11]).filter(Boolean))].sort();
    this.submenusDisponibles2    = [...new Set(this.filtrarSin('submenu2').map(e => e[12]).filter(Boolean))].sort();
    this.submenusDisponibles3    = [...new Set(this.filtrarSin('submenu3').map(e => e[13]).filter(Boolean))].sort();
    this.creadoresDisponibles    = [...new Set(this.filtrarSin('creador').map(e => e[21]).filter(Boolean))].sort();
  }

  // ─── KPIs ────────────────────────────────────────────────────────────────────
  get kpiTotal(): number    { return this.escalamientosFiltrados.length; }
  get kpiSeccionales(): number { return new Set(this.escalamientosFiltrados.map(e => e[15]).filter(Boolean)).size; }
  get kpiNUIs(): number     { return new Set(this.escalamientosFiltrados.map(e => e[14]).filter(Boolean)).size; }

  get kpiPromedioDias(): number {
    if (!this.escalamientosFiltrados.length) return 0;
    const suma = this.escalamientosFiltrados.reduce((acc, e) => acc + (+(e[18] ?? 0)), 0);
    return Math.round((suma / this.escalamientosFiltrados.length) * 10) / 10;
  }

  get kpiCriticos(): number  { return this.escalamientosFiltrados.filter(e => this.getSemaforo(e) === 'Crítico').length; }
  get kpiModerados(): number { return this.escalamientosFiltrados.filter(e => this.getSemaforo(e) === 'Moderado').length; }
  get kpiLeves(): number     { return this.escalamientosFiltrados.filter(e => this.getSemaforo(e) === 'Leve').length; }

  // ─── Semáforo ────────────────────────────────────────────────────────────────
  getSemaforo(e: any): 'Crítico' | 'Moderado' | 'Leve' | 'Cerrado' {
    if (e?.[4] === 'Cerrado') return 'Cerrado';
    const dias = +(e[18] ?? 0);
    if (dias >= 14) return 'Crítico';
    if (dias >= 7)  return 'Moderado';
    return 'Leve';
  }

  getSemaforoClase(e: any): string {
    switch (this.getSemaforo(e)) {
      case 'Cerrado': return 'badge badge-success';
      case 'Crítico':  return 'badge badge-danger';
      case 'Moderado': return 'badge badge-warning';
      default:         return 'badge badge-leve';
    }
  }

  getSemaforoDot(e: any): string {
    switch (this.getSemaforo(e)) {
      case 'Cerrado': return 'dot-green';
      case 'Crítico':  return 'dot-red';
      case 'Moderado': return 'dot-yellow';
      default:         return 'dot-green';
    }
  }

  // ─── Filtros ────────────────────────────────────────────────────────────────
  aplicarFiltros(): void {
    let r = [...this.escalamientos];
    if (this.filtroArea)        r = r.filter(e => e[3] === this.filtroArea);
    if (this.filtroSeccional)   r = r.filter(e => e[15] === this.filtroSeccional);
    if (this.filtroMenu)        r = r.filter(e => e[10] === this.filtroMenu);
    if (this.filtroSubMenu)     r = r.filter(e => e[11] === this.filtroSubMenu);
    if (this.filtroSubMenu2)    r = r.filter(e => e[12] === this.filtroSubMenu2);
    if (this.filtroSubMenu3)    r = r.filter(e => e[13] === this.filtroSubMenu3);
    if (this.filtroEstado)      r = r.filter(e => e[4] === this.filtroEstado);
    if (this.filtroCreador)     r = r.filter(e => e[21] === this.filtroCreador);
    if (this.textoBusqueda.trim()) {
      const t = this.textoBusqueda.toLowerCase().trim();
      r = r.filter(e =>
        (e[1]  + '').toLowerCase().includes(t) ||
        (e[2]  + '').toLowerCase().includes(t) ||
        (e[3]  + '').toLowerCase().includes(t) ||
        (e[8]  + '').toLowerCase().includes(t) ||
        (e[14] + '').toLowerCase().includes(t) ||
        (e[15] + '').toLowerCase().includes(t)
      );
    }
    this.escalamientosFiltrados = this.ordenarArray(r);
    this.paginaActual = 1;
    this.actualizarOpcionesFiltros();
    this.actualizarGraficos();
  }

  limpiarFiltros(): void {
    this.filtroArea        = '';
    this.filtroSeccional   = '';
    this.filtroMenu        = '';
    this.filtroSubMenu     = '';
    this.filtroSubMenu2    = '';
    this.filtroSubMenu3    = '';
    this.filtroEstado      = '';
    this.filtroCreador     = '';
    this.textoBusqueda     = '';
    this.aplicarFiltros();
    this.cdr.markForCheck();
  }

  onFiltroChange(): void {
    this.aplicarFiltros();
    this.cdr.markForCheck();
  }

  hayFiltrosActivos(): boolean {
    return !!(this.filtroArea || this.filtroSeccional || this.filtroMenu || this.filtroSubMenu || this.filtroSubMenu2 || this.filtroSubMenu3 || this.filtroEstado || this.filtroCreador || this.textoBusqueda.trim());
  }

  // ─── Ordenamiento ────────────────────────────────────────────────────────────
  private ordenarArray(datos: any[]): any[] {
    return [...datos].sort((a, b) => {
      let va: any, vb: any;
      switch (this.ordenColumna) {
        case 'ticket':     va = a[1];  vb = b[1];  break;
        case 'nui':        va = a[14]; vb = b[14]; break;
        case 'seccional':  va = a[15]; vb = b[15]; break;
        case 'area':       va = a[3];  vb = b[3];  break;
        case 'estado':     va = a[4];  vb = b[4];  break;
        case 'dias':       va = +(a[18] ?? 0); vb = +(b[18] ?? 0); break;
        default:           va = a[6];  vb = b[6];  // fecha_creacion
      }
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      if (va < vb) return this.ordenAsc ? -1 : 1;
      if (va > vb) return this.ordenAsc ?  1 : -1;
      return 0;
    });
  }

  cambiarOrden(columna: string): void {
    if (this.ordenColumna === columna) {
      this.ordenAsc = !this.ordenAsc;
    } else {
      this.ordenColumna = columna;
      this.ordenAsc = true;
    }
    this.escalamientosFiltrados = this.ordenarArray(this.escalamientosFiltrados);
    this.cdr.markForCheck();
  }

  iconoOrden(columna: string): string {
    if (this.ordenColumna !== columna) return 'fas fa-sort text-muted';
    return this.ordenAsc ? 'fas fa-sort-up' : 'fas fa-sort-down';
  }

  // ─── Paginación ─────────────────────────────────────────────────────────────
  get escalamientosPaginados(): any[] {
    const inicio = (this.paginaActual - 1) * this.registrosPorPagina;
    return this.escalamientosFiltrados.slice(inicio, inicio + this.registrosPorPagina);
  }

  get totalPaginas(): number {
    return Math.ceil(this.escalamientosFiltrados.length / this.registrosPorPagina);
  }

  get paginasVisibles(): number[] {
    const total = this.totalPaginas;
    const actual = this.paginaActual;
    const pages: number[] = [];
    const start = Math.max(1, actual - 2);
    const end = Math.min(total, actual + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  irPagina(p: number): void {
    if (p >= 1 && p <= this.totalPaginas) {
      this.paginaActual = p;
      this.cdr.markForCheck();
    }
  }

  get infoRegistros(): string {
    const total = this.escalamientosFiltrados.length;
    if (!total) return 'Sin resultados';
    const desde = (this.paginaActual - 1) * this.registrosPorPagina + 1;
    const hasta = Math.min(this.paginaActual * this.registrosPorPagina, total);
    return `Mostrando ${desde}–${hasta} de ${total} registros`;
  }

  abrirAdjunto(url: string): void {
    const clean = String(url).replace(/^['"]|['"]$/g, '').trim();
    if (!clean || clean === 'null' || !clean.startsWith('http')) return;
    window.open(encodeURI(clean), '_blank');
  }

  // ─── Cambio individual de estado ─────────────────────────────────────────────
  cambiarEstado(e: any, estado: string): void {
    const prevEstado = e[4];
    if (estado === 'Resuelto') {
      // Actualizar e[4] de inmediato para que el select quede sincronizado;
      // si el usuario cancela, confirmarResolucion revierte usando prevEstado.
      e[4] = estado;
      this.cdr.markForCheck();
      this.confirmarResolucion(e, prevEstado);
      return;
    }
    this.global.consultar({ option: 'actualizar-estado-escalamiento', idEscalamiento: e[0], estadoEscalamiento: estado }, (_err, r) => {
      if (r === true) {
        e[4] = estado;
        this.alerta.descValid('Estado actualizado');
        this.cdr.markForCheck();
      } else {
        this.alerta.descErr('Error al actualizar el estado');
        e[4] = prevEstado;
        this.cdr.markForCheck();
      }
    });
  }

  private confirmarResolucion(e: any, prevEstado: string): void {
    Swal.fire({
      title: 'Resolver escalamiento',
      html: `
        <p style="font-size:.9rem;color:#555;margin-bottom:.75rem;">
          Describe la gestión realizada para resolver el caso <b>#${e[1]}</b>.
        </p>
        <textarea id="swal-desc" class="swal2-textarea" placeholder="Descripción de la resolución *"
          style="width:100%;height:90px;font-size:.88rem;"></textarea>
        <div style="margin-top:.6rem;text-align:left;">
          <label style="font-size:.82rem;color:#555;font-weight:600;">
            <i class="fas fa-paperclip"></i>&nbsp;Adjunto (opcional)
          </label>
          <input type="file" id="swal-file" style="display:block;width:100%;margin-top:.25rem;font-size:.83rem;" />
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#093579',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Marcar como resuelto',
      cancelButtonText: 'Cancelar',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        const desc = (document.getElementById('swal-desc') as HTMLTextAreaElement)?.value?.trim();
        if (!desc) { Swal.showValidationMessage('La descripción es obligatoria'); return false; }

        const fileInput = document.getElementById('swal-file') as HTMLInputElement;
        let urlAdjunto: string | null = null;

        if (fileInput?.files?.length) {
          const form = new FormData();
          form.append('opcion', 'documents');
          form.append('file', fileInput.files[0]);
          form.append('idTicket', String(e[1]));
          try {
            const resp: any = await this.subir.subirarchivo(form).toPromise();
            if (resp?.status) { urlAdjunto = String(resp.url).replace(/^['"]|['"]$/g, ''); }
            else { Swal.showValidationMessage('No se pudo subir el archivo, intenta de nuevo'); return false; }
          } catch {
            Swal.showValidationMessage('Error al subir el archivo'); return false;
          }
        }
        return { desc, urlAdjunto };
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then(result => {
      if (!result.isConfirmed || !result.value) {
        e[4] = prevEstado;
        this.cdr.markForCheck();
        return;
      }
      const payload = result.value as { desc: string; urlAdjunto: string | null };
      this.spinner.show();
      this.global.consultar({
        option: 'resolver-escalamiento',
        idEscalamiento: e[0],
        idticket: e[1],
        descripcionEscalamiento: payload.desc,
        urlAdjunto: payload.urlAdjunto,
        idUsuarioEscalamiento: this.global.Id_Usuario,
      }, (_err, r) => {
        this.spinner.hide();
        if (r === true) {
          e[4]  = 'Resuelto';
          e[20] = payload.urlAdjunto;
          this.alerta.descValid('Escalamiento resuelto y ticket actualizado');
          this.actualizarGraficos();
          this.cdr.markForCheck();
        } else {
          this.alerta.descErr('Error al resolver el escalamiento');
          e[4] = prevEstado;
          this.cdr.markForCheck();
        }
      });
    });
  }

  // ─── Rechazar resolución (solo usuarios idsGestionSAC) ───────────────────────
  rechazarEscalamiento(e: any): void {
    Swal.fire({
      title: 'Rechazar resolución',
      html: `
        <p style="font-size:.9rem;color:#555;margin-bottom:.75rem;">
          ¿Por qué se rechaza la resolución del caso <b>#${e[1]}</b>?<br>
          <small style="color:#888;">El escalamiento quedará en estado <b>Rechazado</b> para revisión del responsable.</small>
        </p>
        <textarea id="swal-motivo-rechazo" class="swal2-textarea"
          placeholder="Motivo del rechazo *" style="min-height:100px;"></textarea>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Confirmar rechazo',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const motivo = (document.getElementById('swal-motivo-rechazo') as HTMLTextAreaElement)?.value?.trim();
        if (!motivo) { Swal.showValidationMessage('El motivo del rechazo es obligatorio'); return false; }
        return motivo;
      },
    }).then(result => {
      if (!result.isConfirmed || !result.value) return;
      const motivo    = result.value as string;
      const motivoSql = motivo.replace(/'/g, "''");
      const _now = new Date();
      const _p   = (n: number) => String(n).padStart(2, '0');
      const fecha = `${_now.getFullYear()}-${_p(_now.getMonth()+1)}-${_p(_now.getDate())} ${_p(_now.getHours())}:${_p(_now.getMinutes())}:${_p(_now.getSeconds())}`;
      this.spinner.show();

      // 1. Volver a Pendiente usando el mismo mecanismo que otros cambios de estado
      this.global.consultar({
        option: 'actualizar-estado-escalamiento',
        idEscalamiento: e[0],
        estadoEscalamiento: 'Pendiente',
      }, (_err, r) => {
        if (r !== true) {
          this.spinner.hide();
          this.alerta.descErr('Error al rechazar el escalamiento');
          return;
        }
        // 2. Registrar en historial del ticket — el prefijo 'CAUSAL DE RECHAZO' se usa
        //    para detectar visualmente el rechazo al cargar escalamientos
        const descSql = `CAUSAL DE RECHAZO: ${motivoSql}`;
        const qInsert = `INSERT INTO db_liwa.Update_Tickets (Id_Ticket, Description, Date, Id_Statuses, IsDelete, IsActive, Id_user) VALUES ('${e[1]}', '${descSql}', '${fecha}', '19', 0, 1, '${this.global.Id_Usuario}')`;
        this.global.consultar({ option: 'insertar3', texto: qInsert }, (_e2, _r2) => {
          this.spinner.hide();
          e[4]  = 'Pendiente';
          e[30] = 1;
          this.alerta.descValid('Escalamiento rechazado. Vuelve a Pendiente.');
          this.actualizarGraficos();
          this.cdr.markForCheck();
        });
      });
    });
  }

  // ─── Gestionar escalamiento ──────────────────────────────────────────────────
  gestionarEscalamiento(e: any): void {
    const areaPaso = e[25] || e[3];
    const accionEsperada = e[26] || '';
    const pasoActual = e[23] || 1;
    const totalPasos = e[24] || 1;
    // Filtrar acciones según la acción esperada del flujo
    const accionesFiltradas = this.getAccionesPorEsperada(accionEsperada);
    Swal.fire({
      title: 'Gestionar escalamiento',
      html: `
        <p style="font-size:.9rem;color:#555;margin-bottom:.5rem;">
          Caso <b>#${e[1]}</b> — Paso <b>${pasoActual}/${totalPasos}</b>
        </p>
        <p style="font-size:.85rem;color:#093579;margin-bottom:.75rem;">
          <i class="fas fa-building"></i> <b>${areaPaso}</b> — Acción esperada: <b>${accionEsperada}</b>
        </p>
        <div style="text-align:left;margin-bottom:.5rem;">
          <label style="font-size:.82rem;color:#555;font-weight:600;">Acción</label>
          <select id="swal-accion" class="swal2-select" style="width:100%;padding:.4em;font-size:.88rem;">
            ${accionesFiltradas.map(a => `<option value="${a}">${a}</option>`).join('')}
          </select>
        </div>
        <textarea id="swal-comentario-gestion" class="swal2-textarea"
          placeholder="Comentario obligatorio *" style="width:100%;height:90px;font-size:.88rem;"></textarea>
        <div style="margin-top:.6rem;text-align:left;">
          <label style="font-size:.82rem;color:#555;font-weight:600;">
            <i class="fas fa-paperclip"></i>&nbsp;Adjunto (opcional)
          </label>
          <input type="file" id="swal-file-gestion" style="display:block;width:100%;margin-top:.25rem;font-size:.83rem;" />
        </div>
      `,
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#093579',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Registrar gestión',
      cancelButtonText: 'Cancelar',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        const accion = (document.getElementById('swal-accion') as HTMLSelectElement)?.value;
        const comentario = (document.getElementById('swal-comentario-gestion') as HTMLTextAreaElement)?.value?.trim();
        if (!comentario) { Swal.showValidationMessage('El comentario es obligatorio'); return false; }

        const fileInput = document.getElementById('swal-file-gestion') as HTMLInputElement;
        let urlAdjunto: string | null = null;
        if (fileInput?.files?.length) {
          const form = new FormData();
          form.append('opcion', 'documents');
          form.append('file', fileInput.files[0]);
          form.append('idTicket', String(e[1]));
          try {
            const resp: any = await this.subir.subirarchivo(form).toPromise();
            if (resp?.status) { urlAdjunto = String(resp.url).replace(/^['"]|['"]$/g, ''); }
            else { Swal.showValidationMessage('No se pudo subir el archivo'); return false; }
          } catch {
            Swal.showValidationMessage('Error al subir el archivo'); return false;
          }
        }
        return { accion, comentario, urlAdjunto };
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then(result => {
      if (!result.isConfirmed || !result.value) return;
      const { accion, comentario, urlAdjunto } = result.value as { accion: string; comentario: string; urlAdjunto: string | null };
      this.spinner.show();
      this.global.consultar({
        option: 'registrar-gestion',
        idEscalamiento: e[0],
        area: areaPaso,
        accion,
        comentario,
        urlAdjunto,
        idUsuarioEscalamiento: this.global.Id_Usuario,
        idticket: e[1],
      }, (_err: any, r: any) => {
        this.spinner.hide();
        if (r === true) {
          if (urlAdjunto) e[20] = urlAdjunto;
          e[5] = comentario;
          // Recargar para obtener paso/área actualizados
          this.cargarEscalamientos();
          this.alerta.descValid(`Gestión registrada: ${accion}`);
        } else {
          this.alerta.descErr('Error al registrar la gestión');
        }
      });
    });
  }

  // ─── Re-tipificar escalamiento (Fase 3) ────────────────────────────────────
  retipificarEscalamiento(e: any): void {
    this.spinner.show();
    this.global.consultar({
      option: 'listar-retipificaciones',
      tipificacion: e[2],
    }, (_err: any, retips: any) => {
      this.spinner.hide();
      if (!Array.isArray(retips) || retips.length === 0) {
        this.alerta.descErr('No hay re-tipificaciones disponibles para esta tipificación.');
        return;
      }
      // retips: [[id_tipo, tipificacion, area_primer_paso, cierre_directo], ...]
      const cierreMap: Record<string, number> = {};
      retips.forEach((r: any) => { cierreMap[String(r[0])] = r[3] || 0; });
      // Tipificaciones con bloqueo de facturación definido automáticamente
      const bloqueoAuto: Record<string, string> = {
        'REPOSICION': 'BLOQUEA FACTURACION',
        'AUSENCIA DE PARTES': 'BLOQUEA FACTURACION',
        'AUSENTE': 'NO BLOQUEA FACTURACION',
        'SISFV FUNCIONAL': 'NO BLOQUEA FACTURACION',
      };
      // Mapeo id_tipo → nombre para resolver bloqueo
      const nombreMap: Record<string, string> = {};
      retips.forEach((r: any) => { nombreMap[String(r[0])] = (r[1] || '').toUpperCase(); });

      const actualizarBloqueo = () => {
        const sel = document.getElementById('swal-retipif') as HTMLSelectElement;
        const divBloqueo = document.getElementById('swal-bloqueo-container');
        if (!sel || !divBloqueo) return;
        const nombre = nombreMap[sel.value] || '';
        divBloqueo.style.display = (nombre in bloqueoAuto) ? 'none' : 'block';
      };

      Swal.fire({
        title: 'Re-tipificar escalamiento',
        html: `
          <p style="font-size:.9rem;color:#555;margin-bottom:.5rem;">
            Caso <b>#${e[1]}</b> — Tipificación actual: <b>${e[2]}</b>
          </p>
          <p style="font-size:.85rem;color:#555;margin-bottom:.75rem;">
            <i class="fas fa-exchange-alt"></i> Seleccione la nueva tipificación para este escalamiento.
          </p>
          <div style="text-align:left;margin-bottom:.5rem;">
            <label style="font-size:.82rem;color:#555;font-weight:600;">Nueva tipificación</label>
            <select id="swal-retipif" class="swal2-select" style="width:100%;padding:.4em;font-size:.88rem;">
              ${retips.map((r: any) => `<option value="${r[0]}">${r[1]}</option>`).join('')}
            </select>
          </div>
          <div id="swal-bloqueo-container" style="text-align:left;margin-bottom:.5rem;">
            <label style="font-size:.82rem;color:#555;font-weight:600;">¿Bloquea facturación?</label>
            <select id="swal-bloqueo" class="swal2-select" style="width:100%;padding:.4em;font-size:.88rem;">
              <option value="BLOQUEA FACTURACION">BLOQUEA FACTURACION</option>
              <option value="NO BLOQUEA FACTURACION" selected>NO BLOQUEA FACTURACION</option>
            </select>
          </div>
          <textarea id="swal-comentario-retipif" class="swal2-textarea"
            placeholder="Comentario obligatorio *" style="width:100%;height:90px;font-size:.88rem;"></textarea>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#093579',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Re-tipificar',
        cancelButtonText: 'Cancelar',
        didOpen: () => {
          actualizarBloqueo();
          const sel = document.getElementById('swal-retipif');
          if (sel) sel.addEventListener('change', actualizarBloqueo);
        },
        preConfirm: () => {
          const select = document.getElementById('swal-retipif') as HTMLSelectElement;
          const comentario = (document.getElementById('swal-comentario-retipif') as HTMLTextAreaElement)?.value?.trim();
          if (!comentario) { Swal.showValidationMessage('El comentario es obligatorio'); return false; }
          const nombre = nombreMap[select.value] || '';
          let subMenu3: string;
          if (nombre in bloqueoAuto) {
            subMenu3 = bloqueoAuto[nombre];
          } else {
            subMenu3 = (document.getElementById('swal-bloqueo') as HTMLSelectElement)?.value || 'NO BLOQUEA FACTURACION';
          }
          return {
            idTipoNuevo: +select.value,
            tipificacionNueva: select.options[select.selectedIndex].text,
            comentario,
            cierreDirecto: cierreMap[select.value] || 0,
            subMenu3,
          };
        },
      }).then(result => {
        if (!result.isConfirmed || !result.value) return;
        const { idTipoNuevo, tipificacionNueva, comentario, cierreDirecto, subMenu3 } = result.value as any;
        this.spinner.show();
        this.global.consultar({
          option: 'retipificar-escalamiento',
          idEscalamiento: e[0],
          idTipoNuevo,
          tipificacionNueva,
          comentario,
          cierreDirecto: !!cierreDirecto,
          subMenu3,
          idUsuarioEscalamiento: this.global.Id_Usuario,
          idticket: e[1],
        }, (_err2: any, r: any) => {
          this.spinner.hide();
          if (r === true) {
            this.cargarEscalamientos();
            this.alerta.descValid(`Escalamiento re-tipificado a: ${tipificacionNueva}`);
          } else {
            this.alerta.descErr('Error al re-tipificar el escalamiento');
          }
        });
      });
    });
  }

  verGestiones(e: any): void {
    this.escalamientoSeleccionado = e;
    this.gestionesEscalamiento = [];
    this.flujoEscalamiento = [];
    this.showModalGestiones = true;
    this.cdr.markForCheck();
    // Cargar gestiones y flujo en paralelo
    this.global.consultar({
      option: 'listar-gestiones-escalamiento',
      idEscalamiento: e[0],
    }, (_err: any, response: any) => {
      if (Array.isArray(response)) {
        this.gestionesEscalamiento = response;
      }
      this.cdr.markForCheck();
    });
    this.global.consultar({
      option: 'obtener-estado-flujo',
      idEscalamiento: e[0],
    }, (_err: any, response: any) => {
      if (Array.isArray(response)) {
        this.flujoEscalamiento = response;
      }
      this.cdr.markForCheck();
    });
  }

  // ─── Exportar Excel ──────────────────────────────────────────────────────────
  exportarExcel(): void {
    const datos = this.escalamientosFiltrados;

    const headers = [
      'ID Ticket', 'NUI', 'Beneficiario', 'Cédula', 'Seccional',
      'Menú', 'SubMenú 1', 'SubMenú 2', 'SubMenú 3',
      'Semáforo', 'Días',
      'Descripción Ticket', 'Creador', 'Fecha Creación',
      'Descripción Escalamiento', 'Área', 'Fecha Asignación',
      'Estado', 'Fecha Respuesta',
    ];
    const fmt = (v: any) => { const d = this.parseFechaLocal(v); return d ? d.toLocaleDateString('es-CO') : ''; };
    const filas = datos.map((e) => [
      e[1], e[14], e[8], e[9], e[15],
      e[10], e[11], e[12], e[13],
      this.getSemaforo(e), e[18] ?? 0,
      (e[22] ?? '-') + '', e[21], fmt(e[6]),
      (e[5] ?? '-') + '', e[3], fmt(e[16]),
      e[4], fmt(e[17]),
    ]);
    this.excelService.exportAsExcelFile2(
      [headers, ...filas],
      'Escalamientos SAC',
      `Escalamientos_SAC_${new Date().toISOString().slice(0, 10)}`
    );
  }

  // ─── Plantilla y carga masiva de actualización de escalamientos ──────────────

  descargarPlantillaActualizacion(): void {
    const datos = [
      ['Id_Ticket', 'Estado', 'Comentario'],
      ['123_1748600000000', 'En Proceso', 'Se contactó al beneficiario y se está gestionando el caso'],
      ['456_1748700000000', 'Resuelto',   'Se resolvió el inconveniente reportado por el cliente'],
    ];
    this.excelService.exportAsExcelFile2(
      datos,
      'Plantilla',
      'Plantilla_Actualizacion_Escalamientos'
    );
  }

  referenciaArchivoCargaMasiva(ev: any): void {
    this.fileCargaMasiva = ev.target.files[0];
    ev.target.value = '';
    if (this.fileCargaMasiva) {
      this.procesarCargaMasivaEscalamientos();
    }
  }

  procesarCargaMasivaEscalamientos(): void {
    if (!this.fileCargaMasiva) return;
    this.spinner.show();
    const fileReader = new FileReader();
    fileReader.onload = () => {
      this.arrayBufferMasivo = fileReader.result;
      const data = new Uint8Array(this.arrayBufferMasivo as ArrayBuffer);
      const arr = Array.from(data).map(d => String.fromCharCode(d));
      const workbook = XLSX.read(arr.join(''), { type: 'binary' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const filas: any[] = XLSX.utils.sheet_to_json(worksheet, { raw: false });

      if (!filas.length) {
        this.alerta.descErr('El archivo está vacío o no tiene datos.');
        this.spinner.hide();
        return;
      }

      const camposRequeridos = ['Id_Ticket', 'Estado', 'Comentario'];
      const faltante = camposRequeridos.find(c => !filas[0]?.hasOwnProperty(c));
      if (faltante) {
        this.alerta.descErr(`Columna requerida no encontrada: "${faltante}". Usa la plantilla oficial.`);
        this.spinner.hide();
        return;
      }

      // Mapa id_ticket → id_escalamiento (usando los escalamientos ya cargados)
      const mapaEscalamientos = new Map<string, string>();
      for (const e of this.escalamientos) {
        mapaEscalamientos.set(String(e[1]).trim(), String(e[0]).trim());
      }

      const estadosValidos = ['En Proceso', 'Resuelto'];
      const errores: string[] = [];

      filas.forEach((row: any, i: number) => {
        const fila = i + 2;
        const idTicket = String(row.Id_Ticket || '').trim();
        const estado   = String(row.Estado    || '').trim();
        const comentario = String(row.Comentario || '').trim();

        if (!idTicket) {
          errores.push(`Fila ${fila}: Id_Ticket está vacío.`);
          return;
        }
        if (!mapaEscalamientos.has(idTicket)) {
          errores.push(`Fila ${fila}: El ticket "${idTicket}" no existe en los escalamientos cargados.`);
        }
        if (!estadosValidos.includes(estado)) {
          errores.push(`Fila ${fila}: Estado "${estado}" inválido. Debe ser "En Proceso" o "Resuelto".`);
        }
        if (!comentario) {
          errores.push(`Fila ${fila}: El comentario está vacío.`);
        }
      });

      if (errores.length) {
        this.spinner.hide();
        this.mostrarResumenErrores(errores);
        return;
      }

      this.datosExcelMasivo = filas;
      this.spinner.hide();
      this.cdr.markForCheck();

      Swal.fire({
        title: 'Confirmar carga masiva',
        html: `Se van a actualizar <b>${filas.length}</b> escalamiento(s).<br><small class="text-muted">Verifica que los datos sean correctos antes de continuar.</small>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#093579',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, actualizar',
        cancelButtonText: 'Cancelar',
      }).then(result => {
        if (result.isConfirmed) {
          this.ejecutarCargaMasivaEscalamientos();
        } else {
          this.datosExcelMasivo = [];
        }
      });
    };
    fileReader.readAsArrayBuffer(this.fileCargaMasiva);
  }

  private ejecutarCargaMasivaEscalamientos(): void {
    if (!this.datosExcelMasivo.length) return;
    this.spinner.show();
    this.procesandoCargaMasiva = true;

    const mapaEscalamientos = new Map<string, string>();
    for (const e of this.escalamientos) {
      mapaEscalamientos.set(String(e[1]).trim(), String(e[0]).trim());
    }

    const errores: string[] = [];
    const total = this.datosExcelMasivo.length;

    const procesarSiguiente = (index: number) => {
      if (index >= total) {
        this.procesandoCargaMasiva = false;
        this.datosExcelMasivo = [];
        this.spinner.hide();
        if (errores.length) {
          this.mostrarResumenErrores(errores, total - errores.length, total);
        } else {
          this.alerta.descValid(`${total} escalamiento(s) actualizados correctamente.`);
        }
        this.cargarEscalamientos();
        this.cdr.markForCheck();
        return;
      }

      const row = this.datosExcelMasivo[index];
      const idTicket   = String(row.Id_Ticket  || '').trim();
      const estado     = String(row.Estado      || '').trim();
      const comentario = String(row.Comentario  || '').trim();
      const idEscalamiento = mapaEscalamientos.get(idTicket);
      const fila = index + 2;

      if (!idEscalamiento) {
        errores.push(`Fila ${fila}: Ticket "${idTicket}" no encontrado al ejecutar.`);
        procesarSiguiente(index + 1);
        return;
      }

      if (estado === 'Resuelto') {
        this.global.consultar({
          option: 'resolver-escalamiento',
          idEscalamiento,
          idticket: idTicket,
          descripcionEscalamiento: comentario,
          urlAdjunto: null,
          idUsuarioEscalamiento: this.global.Id_Usuario,
        }, (_err, r) => {
          if (r !== true) errores.push(`Fila ${fila}: Error al resolver escalamiento del ticket "${idTicket}".`);
          procesarSiguiente(index + 1);
        });
      } else {
        // En Proceso: actualizar estado y guardar comentario
        const sqlUpdate = `UPDATE db_liwa.Escalamientos SET Estado = 'En Proceso', descripcion = '${comentario.replace(/'/g, "''")}' WHERE id_escalamiento = '${idEscalamiento}';`;
        this.global.consultar({ option: 'insertar3', texto: sqlUpdate }, (_err, r) => {
          if (r !== true) errores.push(`Fila ${fila}: Error al actualizar estado del ticket "${idTicket}".`);
          procesarSiguiente(index + 1);
        });
      }
    };

    procesarSiguiente(0);
  }

  private mostrarResumenErrores(errores: string[], exitosos?: number, total?: number): void {
    const maxMostrar = 10;
    const listItems = errores.slice(0, maxMostrar)
      .map(e => `<li style="text-align:left;font-size:.85rem;">${e}</li>`)
      .join('');
    const hayMas = errores.length > maxMostrar
      ? `<li style="text-align:left;font-size:.85rem;color:#6c757d;">...y ${errores.length - maxMostrar} error(es) más</li>`
      : '';
    const resumen = exitosos !== undefined && total !== undefined
      ? `<p style="margin-bottom:.5rem;"><b>${exitosos} de ${total}</b> escalamiento(s) procesados correctamente.</p>`
      : '';

    Swal.fire({
      title: `Se encontraron ${errores.length} error(es)`,
      html: `${resumen}<ul style="max-height:200px;overflow-y:auto;padding-left:1.2rem;margin-bottom:0;">${listItems}${hayMas}</ul>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#093579',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Descargar log Excel',
      cancelButtonText: 'Cerrar',
    }).then(result => {
      if (result.isConfirmed) {
        this.descargarLogErrores(errores);
      }
    });
  }

  private descargarLogErrores(errores: string[]): void {
    const datos = [
      ['Fila', 'Error'],
      ...errores.map(e => {
        const match = e.match(/^Fila (\d+): (.+)$/);
        return match ? [match[1], match[2]] : ['-', e];
      }),
    ];
    this.excelService.exportAsExcelFile2(
      datos,
      'Errores',
      `Log_Errores_Escalamientos_${new Date().toISOString().slice(0, 10)}`
    );
  }

  // ─── Escalamiento masivo ─────────────────────────────────────────────────────
  escalarTicketsAbiertos(): void {
    this.spinner.show();
    this.global.consultar({ option: 'preview-escalamiento-masivo', idCliente: this.global.Id_Client }, (_err, response) => {
      this.spinner.hide();
      if (!Array.isArray(response) || !response[0]) {
        const msg = response?.sqlMessage || response?.message || 'Error al obtener información de tickets';
        this.alerta.descErr(msg);
        return;
      }
      const [ticketsAbiertos, , puedenEscalarse, sinTipificacion, yaEscalados, escalamientosASincronizar] = response[0];
      const filaSync = (escalamientosASincronizar > 0)
        ? `<tr style="background:#fff3cd;"><td style="padding:8px 12px;color:#856404;">Escalamientos a sincronizar (ticket cerrado)</td><td style="padding:8px 12px;font-weight:bold;text-align:right;color:#856404;">${escalamientosASincronizar}</td></tr>`
        : '';
      const hayAccion = puedenEscalarse > 0 || escalamientosASincronizar > 0;
      let btnLabel = '';
      if (puedenEscalarse > 0 && escalamientosASincronizar > 0) {
        btnLabel = `Escalar ${puedenEscalarse} ticket(s) y sincronizar ${escalamientosASincronizar}`;
      } else if (puedenEscalarse > 0) {
        btnLabel = `Escalar ${puedenEscalarse} ticket(s)`;
      } else if (escalamientosASincronizar > 0) {
        btnLabel = `Sincronizar ${escalamientosASincronizar} escalamiento(s)`;
      }
      Swal.fire({
        title: '<strong>Escalar tickets abiertos</strong>',
        icon: 'info',
        html: `
          <table style="width:100%;border-collapse:collapse;font-size:.95rem;">
            <tr style="background:#f8f9fa;"><td style="padding:8px 12px;color:#555;">Tickets abiertos</td><td style="padding:8px 12px;font-weight:bold;text-align:right;">${ticketsAbiertos}</td></tr>
            <tr><td style="padding:8px 12px;color:#555;">Ya escalados</td><td style="padding:8px 12px;font-weight:bold;text-align:right;color:#6c757d;">${yaEscalados}</td></tr>
            <tr style="background:#f8f9fa;"><td style="padding:8px 12px;color:#555;">Sin tipificación asignable</td><td style="padding:8px 12px;font-weight:bold;text-align:right;color:#fd7e14;">${sinTipificacion}</td></tr>
            ${filaSync}
            <tr style="background:#e8f4f8;"><td style="padding:8px 12px;font-weight:bold;color:#0c6291;">Se crearán escalamientos</td><td style="padding:8px 12px;font-weight:bold;text-align:right;font-size:1.2rem;color:#0c6291;">${puedenEscalarse}</td></tr>
          </table>
          ${!hayAccion ? '<p class="mt-3 text-muted" style="font-size:.85rem;">No hay tickets pendientes de escalamiento.</p>' : ''}
        `,
        showCancelButton: true,
        confirmButtonColor: '#093579',
        cancelButtonColor: '#6c757d',
        confirmButtonText: hayAccion ? btnLabel : 'Cerrar',
        cancelButtonText: 'Cancelar',
        showConfirmButton: hayAccion,
      }).then(result => {
        if (!result.isConfirmed) return;
        this.spinner.show();
        this.global.consultar({ option: 'ejecutar-escalamiento-masivo', idCliente: this.global.Id_Client }, (_e2, r2) => {
          this.spinner.hide();
          if (!r2) { this.alerta.descErr('Error al ejecutar escalamiento masivo'); return; }
          const msgSync = (r2.sincronizados > 0) ? ` y se sincronizaron <b>${r2.sincronizados}</b> cerrado(s)` : '';
          Swal.fire({
            icon: 'success', title: '¡Listo!',
            html: `Se crearon <b>${r2.insertados ?? 0}</b> escalamiento(s)${msgSync}.`,
            confirmButtonColor: '#093579',
          }).then(() => this.cargarEscalamientos());
        });
      });
    });
  }

  // ─── Gráficos ────────────────────────────────────────────────────────────────
  private actualizarGraficos(): void {
    this.buildChartSeccional();
    this.buildChartEstado();
    this.buildChartAreas();
    this.buildChartTendencia();
    this.buildChartCategoria();
    this.buildChartSubMenu(1, 11, '#26C6DA');
    this.buildChartSubMenu(2, 12, '#FF69B4');
    this.buildChartSubMenu(3, 13, '#FFA726');
    this.buildChartDistAreas();
    this.buildChartEstadoGestion();
    this.buildChartHeatmap();
    this.buildChartAntiguedad();
  }

  private buildChartSeccional(): void {
    const mapa = new Map<string, number>();
    this.escalamientosFiltrados.forEach(e => {
      const s = e[15] || 'Sin seccional';
      mapa.set(s, (mapa.get(s) ?? 0) + 1);
    });
    const sorted = Array.from(mapa.entries()).sort((a, b) => b[1] - a[1]);
    this.chartSeccionalTotal = sorted.length;
    const visible = this.chartSeccionalExpandido ? sorted : sorted.slice(0, 15);
    const alturaPorFila = 28;
    this.chartSeccionalAltura = Math.max(300, visible.length * alturaPorFila);
    this.chartSeccional = {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '3%', right: '5%', bottom: '3%', top: '3%', containLabel: true },
      xAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed' } } },
      yAxis: { type: 'category', data: visible.map(x => x[0]), axisLabel: { fontSize: 11 } },
      series: [{
        type: 'bar', data: visible.map(x => x[1]),
        itemStyle: { color: '#4A90D9', borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: 'right', fontSize: 11 },
      }],
    };
  }

  toggleSeccional(): void {
    this.chartSeccionalExpandido = !this.chartSeccionalExpandido;
    this.buildChartSeccional();
    this.cdr.markForCheck();
  }

  private buildChartEstado(): void {
    const colores: Record<string, string> = {
      'Pendiente':          '#ffc107',
      'En Proceso':         '#4A90D9',
      'Gestionado':         '#093579',
      'Resuelto':           '#28a745',
      'Cerrado':            '#6c757d',
    };
    const mapa = new Map<string, number>();
    this.estadosDisponibles.forEach(est => mapa.set(est, 0));
    mapa.set('Gestionado', 0);
    this.escalamientosFiltrados.forEach(e => {
      if (mapa.has(e[4])) mapa.set(e[4], mapa.get(e[4])! + 1);
    });
    this.chartEstado = {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { bottom: '0%', left: 'center', textStyle: { fontSize: 10 } },
      series: [{
        type: 'pie', radius: ['38%', '65%'],
        center: ['50%', '44%'],
        avoidLabelOverlap: false,
        label: { show: true, fontSize: 10, formatter: '{b}\n{c}' },
        data: Array.from(mapa.entries())
          .filter(([, v]) => v > 0)
          .map(([name, value]) => ({ name, value, itemStyle: { color: colores[name] ?? '#aaa' } })),
      }],
    };
  }

  private buildChartAreas(): void {
    const mapa = new Map<string, number>();
    this.escalamientosFiltrados.forEach(e => {
      const r = e[3] || 'Sin asignar';
      mapa.set(r, (mapa.get(r) ?? 0) + 1);
    });
    const sorted = Array.from(mapa.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10);
    this.chartResponsables = {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '3%', right: '5%', bottom: '3%', top: '5%', containLabel: true },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: sorted.map(x => x[0]), axisLabel: { fontSize: 11 } },
      series: [{
        type: 'bar', data: sorted.map(x => x[1]),
        itemStyle: { color: '#7B68EE', borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: 'right', fontSize: 11 },
      }],
    };
  }

  private buildChartTendencia(): void {
    const mapa = new Map<string, number>();
    this.escalamientosFiltrados.forEach(e => {
      if (!e[6]) return;
      const d = this.parseFechaLocal(e[6]);
      if (!d) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      mapa.set(key, (mapa.get(key) ?? 0) + 1);
    });
    const sorted = Array.from(mapa.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    this.chartTendencia = {
      tooltip: { trigger: 'axis' },
      grid: { left: '3%', right: '4%', bottom: '10%', top: '5%', containLabel: true },
      xAxis: { type: 'category', data: sorted.map(x => x[0]), axisLabel: { rotate: 45, fontSize: 11 } },
      yAxis: { type: 'value' },
      series: [{
        type: 'line', data: sorted.map(x => x[1]),
        smooth: true, symbol: 'circle', symbolSize: 6,
        lineStyle: { color: '#FF69B4', width: 2 },
        itemStyle: { color: '#FF69B4' },
        areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(255,105,180,.4)' }, { offset: 1, color: 'rgba(255,105,180,.05)' }] } },
      }],
    };
  }

  private buildChartCategoria(): void {
    const mapa = new Map<string, number>();
    this.escalamientosFiltrados.forEach(e => {
      const m = e[10] || 'Sin categoría';
      mapa.set(m, (mapa.get(m) ?? 0) + 1);
    });
    const sorted = Array.from(mapa.entries()).sort((a, b) => b[1] - a[1]);
    const colores = ['#4A90D9', '#7B68EE', '#FF69B4', '#FFA726', '#26C6DA'];
    this.chartCategoriaAltura = Math.max(300, sorted.length * 36);
    this.chartCategoria = {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '3%', right: '5%', bottom: '3%', top: '5%', containLabel: true },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: sorted.map(x => x[0]) },
      series: [{
        type: 'bar', data: sorted.map((x, i) => ({ value: x[1], itemStyle: { color: colores[i % colores.length], borderRadius: [0, 4, 4, 0] } })),
        label: { show: true, position: 'right', fontSize: 12, fontWeight: 'bold' },
      }],
    };
  }

  private buildChartDistAreas(): void {
    const mapa = new Map<string, number>();
    this.escalamientosFiltrados.forEach(e => {
      const r = e[3] || 'Sin asignar';
      mapa.set(r, (mapa.get(r) ?? 0) + 1);
    });
    const sorted = Array.from(mapa.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10);
    const colores = ['#093579','#4A90D9','#7B68EE','#26C6DA','#FF69B4','#FFA726','#66BB6A','#EF5350','#AB47BC','#26A69A'];
    this.chartDistAreasAltura = Math.max(300, sorted.length * 36);
    this.chartDistAreas = {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '3%', right: '8%', bottom: '3%', top: '5%', containLabel: true },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: sorted.map(x => x[0]), axisLabel: { fontSize: 10 } },
      series: [{
        type: 'bar',
        data: sorted.map((x, i) => ({ value: x[1], itemStyle: { color: colores[i % colores.length], borderRadius: [0, 4, 4, 0] } })),
        label: { show: true, position: 'right', fontSize: 11, fontWeight: 'bold' },
      }],
    };
  }

  private buildChartEstadoGestion(): void {
    const colores: Record<string, string> = {
      'Pendiente':          '#ffc107',
      'En Proceso':         '#4A90D9',
      'Gestionado':         '#093579',
      'Resuelto':           '#28a745',
      'Cerrado':            '#6c757d',
    };
    const mapa = new Map<string, number>();
    this.estadosDisponibles.forEach(est => mapa.set(est, 0));
    mapa.set('Gestionado', 0);
    this.escalamientosFiltrados.forEach(e => {
      const est = e[4];
      if (mapa.has(est)) mapa.set(est, mapa.get(est)! + 1);
    });
    this.chartEstadoGestion = {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { bottom: '0%', left: 'center', textStyle: { fontSize: 10 } },
      series: [{
        type: 'pie', radius: ['38%', '65%'],
        center: ['50%', '44%'],
        avoidLabelOverlap: false,
        label: { show: true, fontSize: 10, formatter: '{b}\n{c}' },
        data: Array.from(mapa.entries())
          .filter(([, v]) => v > 0)
          .map(([name, value]) => ({ name, value, itemStyle: { color: colores[name] ?? '#aaa' } })),
      }],
    };
  }

  private buildChartHeatmap(): void {
    const meses = new Set<string>();
    const seccionales = new Set<string>();
    this.escalamientosFiltrados.forEach(e => {
      if (e[6]) {
        const d = this.parseFechaLocal(e[6]);
        if (d) meses.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
      if (e[15]) seccionales.add(e[15]);
    });
    const mesesArr = Array.from(meses).sort();
    const seccionalesArr = Array.from(seccionales).sort();
    const countMap = new Map<string, number>();
    this.escalamientosFiltrados.forEach(e => {
      if (!e[6] || !e[15]) return;
      const d = this.parseFechaLocal(e[6]);
      if (!d) return;
      const mes = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const key = `${e[15]}__${mes}`;
      countMap.set(key, (countMap.get(key) ?? 0) + 1);
    });
    const data: any[] = [];
    seccionalesArr.forEach((sec, yi) => {
      mesesArr.forEach((mes, xi) => {
        const val = countMap.get(`${sec}__${mes}`) ?? 0;
        if (val > 0) data.push([xi, yi, val]);
      });
    });
    const maxVal = data.length ? Math.max(...data.map(d => d[2])) : 1;
    this.chartHeatmap = {
      tooltip: {
        position: 'top',
        formatter: (p: any) => `<b>${seccionalesArr[p.data[1]]}</b><br>${mesesArr[p.data[0]]}: <b>${p.data[2]} tickets</b>`,
      },
      grid: { left: '2%', right: '2%', bottom: '15%', top: '3%', containLabel: true },
      xAxis: { type: 'category', data: mesesArr, axisLabel: { rotate: 45, fontSize: 10 } },
      yAxis: { type: 'category', data: seccionalesArr, axisLabel: { fontSize: 10 } },
      visualMap: {
        min: 0, max: maxVal, calculable: true,
        orient: 'horizontal', left: 'center', bottom: '2%',
        inRange: { color: ['#e8f4f8', '#4A90D9', '#093579'] },
      },
      series: [{
        type: 'heatmap', data,
        label: { show: true, fontSize: 9 },
        emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,.5)' } },
      }],
    };
  }

  private buildChartAntiguedad(): void {
    const grupos = [
      { label: '0 – 5 días',    min: 0,   max: 5,        color: '#26C6DA' },
      { label: '6 – 15 días',   min: 6,   max: 15,       color: '#66BB6A' },
      { label: '16 – 30 días',  min: 16,  max: 30,       color: '#FFA726' },
      { label: '31 – 60 días',  min: 31,  max: 60,       color: '#EF5350' },
      { label: '61 – 100 días', min: 61,  max: 100,      color: '#AB47BC' },
      { label: '101 – 150 días',min: 101, max: 150,      color: '#5C6BC0' },
      { label: '+150 días',     min: 151, max: Infinity,  color: '#37474F' },
    ];
    const counts = grupos.map(g =>
      this.escalamientosFiltrados.filter(e => {
        const d = +(e[18] ?? 0);
        return d >= g.min && d <= g.max;
      }).length
    );
    this.chartAntiguedad = {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: (p: any) => `${p[0].name}: <b>${p[0].value} tickets</b>` },
      grid: { left: '3%', right: '5%', bottom: '5%', top: '10%', containLabel: true },
      xAxis: { type: 'category', data: grupos.map(g => g.label), axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', minInterval: 1 },
      series: [{
        type: 'bar',
        data: counts.map((v, i) => ({ value: v, itemStyle: { color: grupos[i].color, borderRadius: [4, 4, 0, 0] } })),
        label: { show: true, position: 'top', fontSize: 13, fontWeight: 'bold' },
      }],
    };
  }

  private buildChartSubMenu(nivel: 1 | 2 | 3, idx: number, color: string): void {
    const mapa = new Map<string, number>();
    this.escalamientosFiltrados.forEach(e => {
      const v = e[idx];
      if (v) mapa.set(v, (mapa.get(v) ?? 0) + 1);
    });
    const sorted = Array.from(mapa.entries()).sort((a, b) => b[1] - a[1]);

    if (nivel === 2) {
      this.chartSubMenu2Total = sorted.length;
      const visible = this.chartSubMenu2Expandido ? sorted : sorted.slice(0, 15);
      this.chartSubMenu2Altura = Math.max(300, visible.length * 36);
      this.chartSubMenu2 = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: '3%', right: '8%', bottom: '3%', top: '3%', containLabel: true },
        xAxis: { type: 'value' },
        yAxis: { type: 'category', data: visible.map(x => x[0]), axisLabel: { fontSize: 10, width: 160, overflow: 'truncate' } },
        series: [{ type: 'bar', data: visible.map(x => x[1]), itemStyle: { color, borderRadius: [0, 4, 4, 0] }, label: { show: true, position: 'right', fontSize: 11 } }],
      };
      return;
    }

    const altura = Math.max(300, sorted.length * 36);
    const chart = {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '3%', right: '8%', bottom: '3%', top: '5%', containLabel: true },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: sorted.map(x => x[0]), axisLabel: { fontSize: 10, width: 160, overflow: 'truncate' } },
      series: [{ type: 'bar', data: sorted.map(x => x[1]), itemStyle: { color, borderRadius: [0, 4, 4, 0] }, label: { show: true, position: 'right', fontSize: 11 } }],
    };
    if (nivel === 1) { this.chartSubMenu1 = chart; this.chartSubMenu1Altura = altura; }
    else { this.chartSubMenu3 = chart; this.chartSubMenu3Altura = altura; }
  }

  toggleSubMenu2(): void {
    this.chartSubMenu2Expandido = !this.chartSubMenu2Expandido;
    this.buildChartSubMenu(2, 12, '#FF69B4');
    this.cdr.markForCheck();
  }

  // ─── Exportar PDF ────────────────────────────────────────────────────────────
  exportarPDF(): void {
    this.spinner.show();
    setTimeout(() => {
      try {
        this._generarPDF();
      } catch (err) {
        console.error('Error PDF:', err);
        this.alerta.descErr('Error al generar el PDF');
      } finally {
        this.spinner.hide();
        this.cdr.markForCheck();
      }
    }, 100);
  }

  private _renderChart(option: any, w = 1200, h = 420): string {
    const div = document.createElement('div');
    div.style.cssText = `width:${w}px;height:${h}px;position:absolute;left:-9999px;top:0;visibility:hidden;`;
    document.body.appendChild(div);
    const inst = (echarts as any).init(div, null, { renderer: 'canvas', width: w, height: h });
    // Clonar opciones via JSON para evitar referencias compartidas y deshabilitar animaciones
    const opt = JSON.parse(JSON.stringify(option));
    opt.animation = false;
    opt.animationDuration = 0;
    opt.animationDurationUpdate = 0;
    inst.setOption(opt);
    const url = inst.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
    inst.dispose();
    document.body.removeChild(div);
    return url;
  }

  private _pdfHdr(doc: any, titulo: string, pagina: number, W: number): void {
    doc.setFillColor(9, 53, 121);
    doc.rect(0, 0, W, 11, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFontStyle('bold');
    doc.text('GESTIÓN ESCALAMIENTOS SAC — ENGENIUS', 10, 7.5);
    doc.setFontStyle('normal');
    doc.text(titulo, W / 2, 7.5, 'center');
    doc.text(`Pág. ${pagina}`, W - 10, 7.5, 'right');
    doc.setTextColor(0, 0, 0);
  }

  private _pdfFtr(doc: any, fecha: string, W: number, H: number): void {
    doc.setDrawColor(200, 200, 200);
    doc.line(10, H - 6, W - 10, H - 6);
    doc.setTextColor(140, 140, 140);
    doc.setFontSize(6.5);
    doc.text(`Generado: ${fecha}  |  Confidencial – solo uso interno`, 10, H - 3);
    doc.setTextColor(0, 0, 0);
  }

  private _buildFiltrosTexto(): string {
    const partes: string[] = [];
    if (this.filtroArea) partes.push(`Área: ${this.filtroArea}`);
    if (this.filtroSeccional)   partes.push(`Seccional: ${this.filtroSeccional}`);
    if (this.filtroMenu)        partes.push(`Menú: ${this.filtroMenu}`);
    if (this.filtroSubMenu)     partes.push(`SubMenú 1: ${this.filtroSubMenu}`);
    if (this.filtroSubMenu2)    partes.push(`SubMenú 2: ${this.filtroSubMenu2}`);
    if (this.filtroSubMenu3)    partes.push(`SubMenú 3: ${this.filtroSubMenu3}`);
    if (this.filtroEstado)      partes.push(`Estado: ${this.filtroEstado}`);
    if (this.filtroCreador)     partes.push(`Creador: ${this.filtroCreador}`);
    if (this.textoBusqueda.trim()) partes.push(`Búsqueda: "${this.textoBusqueda.trim()}"`);
    return partes.length ? partes.join('  |  ') : 'Sin filtros (datos completos)';
  }

  private _generarPDF(): void {
    const doc   = new (jsPDFLib as any)('landscape', 'mm', 'a4');
    const W     = 297;   // ancho A4 landscape
    const H     = 210;   // alto  A4 landscape
    const ML    = 10;    // margen izquierdo
    const CW    = W - ML * 2; // ancho contenido
    const now   = new Date();
    const fecha = now.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })
                + ' ' + now.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

    // ── Tablas auxiliares ────────────────────────────────────────────
    const secMap  = new Map<string, number>();
    const respMap = new Map<string, number>();
    const menuMap = new Map<string, number>();
    const sub1Map = new Map<string, number>();
    const sub2Map = new Map<string, number>();
    const sub3Map = new Map<string, number>();
    this.escalamientosFiltrados.forEach(e => {
      if (e[15]) secMap.set(e[15],   (secMap.get(e[15])   ?? 0) + 1);
      if (e[3])  respMap.set(e[3],   (respMap.get(e[3])   ?? 0) + 1);
      if (e[10]) menuMap.set(e[10],  (menuMap.get(e[10])  ?? 0) + 1);
      if (e[11]) sub1Map.set(e[11],  (sub1Map.get(e[11])  ?? 0) + 1);
      if (e[12]) sub2Map.set(e[12],  (sub2Map.get(e[12])  ?? 0) + 1);
      if (e[13]) sub3Map.set(e[13],  (sub3Map.get(e[13])  ?? 0) + 1);
    });
    const sortMap  = (m: Map<string, number>) => Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
    const secArr   = sortMap(secMap);
    const respArr  = sortMap(respMap);
    const menuArr  = sortMap(menuMap);
    const sub1Arr  = sortMap(sub1Map);
    const sub2Arr  = sortMap(sub2Map);
    const sub3Arr  = sortMap(sub3Map);
    const total    = this.kpiTotal;
    const pct = (n: number) => total > 0 ? `${((n / total) * 100).toFixed(2)}%` : '0.00%';

    const estadosRow = ['Pendiente', 'En Proceso', 'Resuelto', 'Cerrado'].map(est => {
      const c = this.escalamientosFiltrados.filter(e => e[4] === est).length;
      return [est, c, pct(c)];
    });

    // ── Opciones de charts para PDF (offscreen) ──────────────────────
    // Seccional: TODOS los datos (no limitado a 15)
    const secH = Math.max(450, secArr.length * 28);
    const optSeccionalFull = {
      ...this.chartSeccional,
      yAxis: { type: 'category', data: secArr.map(x => x[0]), axisLabel: { fontSize: 11 } },
      series: [{ ...(this.chartSeccional.series || [{}])[0], data: secArr.map(x => x[1]) }],
    };

    // ── Render de todas las gráficas ─────────────────────────────────
    const imgSeccional    = this._renderChart(optSeccionalFull, 1300, secH);
    const imgEstado       = this._renderChart(this.chartEstado,          700, 450);
    const imgEstadoGest   = this._renderChart(this.chartEstadoGestion,   700, 450);
    const imgResponsables = this._renderChart(this.chartDistAreas,1100, 420);
    const imgMenu         = this._renderChart(this.chartCategoria,       1100, 420);
    const imgSub1         = this._renderChart(this.chartSubMenu1,        1100, 420);
    const imgSub2         = this._renderChart(this.chartSubMenu2,        1100, 420);
    const imgSub3         = this._renderChart(this.chartSubMenu3,        1100, 420);
    const imgAntiguedad   = this._renderChart(this.chartAntiguedad,      1300, 380);
    const imgTendencia    = this._renderChart(this.chartTendencia,       1300, 380);
    const imgHeatmap      = this._renderChart(this.chartHeatmap,         1400, 500);

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // PÁGINA 1 — PORTADA / RESUMEN EJECUTIVO
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    let pg = 1;
    this._pdfHdr(doc, 'Resumen Ejecutivo', pg, W);

    // Banner título
    doc.setFillColor(9, 53, 121);
    doc.rect(0, 11, W, 18, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFontStyle('bold');
    doc.text('Reporte de Gestión — Escalamientos SAC', W / 2, 21, 'center');
    doc.setFontSize(8);
    doc.setFontStyle('normal');
    doc.text(fecha, W / 2, 27, 'center');
    doc.setTextColor(0, 0, 0);

    // Filtros
    let y = 33;
    doc.setFontSize(7.5);
    doc.setTextColor(80, 80, 80);
    const filtrosTxt = this._buildFiltrosTexto();
    doc.text(`Filtros: ${filtrosTxt}`, ML, y, { maxWidth: CW });
    y += 6;

    // KPI cards (6 cajas)
    const kpis = [
      { label: 'Total',       val: String(total),             color: [9, 53, 121]   as [number,number,number] },
      { label: 'Seccionales', val: String(this.kpiSeccionales), color: [23, 162, 184] as [number,number,number] },
      { label: 'NUIs únicos', val: String(this.kpiNUIs),       color: [40, 167, 69]  as [number,number,number] },
      { label: 'Prom. días',  val: String(this.kpiPromedioDias), color: [102, 16, 242] as [number,number,number] },
      { label: 'Críticos',   val: String(this.kpiCriticos),   color: [220, 53, 69]  as [number,number,number] },
      { label: 'Moderados',   val: String(this.kpiModerados),  color: [255, 140, 0]  as [number,number,number] },
    ];
    const bw = CW / 6;
    kpis.forEach((k, i) => {
      const bx = ML + i * bw;
      doc.setFillColor(...k.color);
      doc.roundedRect(bx, y, bw - 1.5, 18, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(15);
      doc.setFontStyle('bold');
      doc.text(k.val, bx + (bw - 1.5) / 2, y + 10, 'center');
      doc.setFontSize(6.5);
      doc.setFontStyle('normal');
      doc.text(k.label, bx + (bw - 1.5) / 2, y + 16, 'center');
    });
    doc.setTextColor(0, 0, 0);
    y += 23;

    // Tres tablas en columnas: Estado | Top 8 Seccionales | Top 8 Áreas
    const tW1 = 55, tW2 = 100, tW3 = CW - tW1 - tW2 - 8;
    const tblOpts = { startY: y, styles: { fontSize: 7.5, cellPadding: 1.8 },
      headStyles: { fillColor: [9, 53, 121], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [240, 245, 255] } };

    (doc as any).autoTable({ ...tblOpts,
      margin: { left: ML }, tableWidth: tW1,
      head: [['Estado', 'N°', '%']], body: estadosRow,
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } },
    });
    (doc as any).autoTable({ ...tblOpts,
      margin: { left: ML + tW1 + 4 }, tableWidth: tW2,
      head: [['Top Seccionales', 'N°', '%']],
      body: secArr.slice(0, 8).map(([s, c]) => [s, c, pct(c)]),
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } },
    });
    (doc as any).autoTable({ ...tblOpts,
      margin: { left: ML + tW1 + tW2 + 8 }, tableWidth: tW3,
      head: [['Top Áreas', 'Asignados', '%']],
      body: respArr.slice(0, 8).map(([r, c]) => [r, c, pct(c)]),
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } },
    });

    this._pdfFtr(doc, fecha, W, H);

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // PÁGINA 2 — TICKETS POR SECCIONAL (todos los datos)
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    doc.addPage();
    pg++;
    this._pdfHdr(doc, 'Tickets por Seccional', pg, W);
    doc.setFontSize(8.5); doc.setFontStyle('bold');
    doc.text(`Tickets por Seccional — ${secArr.length} seccionales`, ML, 16);
    doc.setFontStyle('normal');

    // Imagen de la gráfica (escala proporcional para que quepa)
    const secImgH = Math.min(H - 26, CW * (secH / 1300));
    doc.addImage(imgSeccional, 'PNG', ML, 18, CW, secImgH);

    // Si hay muchas seccionales, agregar tabla resumen al lado
    if (secArr.length <= 30) {
      // tabla completa debajo si cabe, sino ya está la gráfica
    }
    this._pdfFtr(doc, fecha, W, H);

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // PÁGINA 3 — SEMÁFORO + ESTADO DE GESTIÓN
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    doc.addPage(); pg++;
    this._pdfHdr(doc, 'Semáforo y Estado de Gestión', pg, W);
    const halfW = CW / 2;
    const chartY = 14;
    const chartH = H - chartY - 10;

    doc.setFontSize(8); doc.setFontStyle('bold');
    doc.text('Estado del Servicio (Semáforo)', ML, chartY + 5);
    doc.text('Distribución por Estado de Gestión', ML + halfW + 4, chartY + 5);
    doc.setFontStyle('normal');

    doc.addImage(imgEstado,     'PNG', ML,               chartY + 7, halfW - 2,  chartH - 7);
    doc.addImage(imgEstadoGest, 'PNG', ML + halfW + 4,  chartY + 7, halfW - 6,  chartH - 7);
    this._pdfFtr(doc, fecha, W, H);

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // PÁGINA 4 — RESPONSABLES + MENÚ/CATEGORÍA
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    doc.addPage(); pg++;
    this._pdfHdr(doc, 'Áreas y Categorías', pg, W);
    const ch4 = H - 24;

    doc.setFontSize(8); doc.setFontStyle('bold');
    doc.text('Distribución por Áreas', ML, 17);
    doc.text('Tickets por Categoría (Menú)', ML + halfW + 4, 17);
    doc.setFontStyle('normal');

    doc.addImage(imgResponsables, 'PNG', ML,              18, halfW - 2, ch4);
    doc.addImage(imgMenu,         'PNG', ML + halfW + 4, 18, halfW - 6, ch4);

    // Tablas resumen debajo
    // (autoTable con startY calculado)
    this._pdfFtr(doc, fecha, W, H);

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // PÁGINA 5 — SUBMENÚ 1 + SUBMENÚ 2
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    doc.addPage(); pg++;
    this._pdfHdr(doc, 'SubMenú 1 y SubMenú 2', pg, W);
    const ch5 = H - 24;

    doc.setFontSize(8); doc.setFontStyle('bold');
    doc.text('SubMenú 1', ML, 17);
    doc.text('SubMenú 2', ML + halfW + 4, 17);
    doc.setFontStyle('normal');

    doc.addImage(imgSub1, 'PNG', ML,              18, halfW - 2, ch5);
    doc.addImage(imgSub2, 'PNG', ML + halfW + 4, 18, halfW - 6, ch5);
    this._pdfFtr(doc, fecha, W, H);

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // PÁGINA 6 — SUBMENÚ 3 + ANTIGÜEDAD
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    doc.addPage(); pg++;
    this._pdfHdr(doc, 'SubMenú 3 y Antigüedad', pg, W);
    const ch6 = H - 24;

    doc.setFontSize(8); doc.setFontStyle('bold');
    doc.text('SubMenú 3', ML, 17);
    doc.text('Tickets agrupados por Antigüedad', ML + halfW + 4, 17);
    doc.setFontStyle('normal');

    doc.addImage(imgSub3,       'PNG', ML,              18, halfW - 2, ch6);
    doc.addImage(imgAntiguedad, 'PNG', ML + halfW + 4, 18, halfW - 6, ch6);
    this._pdfFtr(doc, fecha, W, H);

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // PÁGINA 7 — TENDENCIA DE CREACIÓN
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    doc.addPage(); pg++;
    this._pdfHdr(doc, 'Tendencia de Creación de Tickets', pg, W);
    doc.setFontSize(8); doc.setFontStyle('bold');
    doc.text('Tendencia de Creación de Escalamientos por Mes', ML, 17);
    doc.setFontStyle('normal');
    doc.addImage(imgTendencia, 'PNG', ML, 19, CW, H - 28);
    this._pdfFtr(doc, fecha, W, H);

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // PÁGINA 8 — HEATMAP SECCIONAL × MES
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    doc.addPage(); pg++;
    this._pdfHdr(doc, 'Heatmap Seccional × Mes', pg, W);
    doc.setFontSize(8); doc.setFontStyle('bold');
    doc.text('Mapa de Calor — Tickets por Seccional y Mes', ML, 17);
    doc.setFontStyle('normal');
    doc.addImage(imgHeatmap, 'PNG', ML, 19, CW, H - 28);
    this._pdfFtr(doc, fecha, W, H);

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // PÁGINA 9 — TABLAS DETALLADAS
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    doc.addPage(); pg++;
    this._pdfHdr(doc, 'Tablas de Detalle', pg, W);
    doc.setFontSize(9); doc.setFontStyle('bold');
    doc.text('Detalle por Categorías', ML, 17);
    doc.setFontStyle('normal');

    // Tabla completa seccionales + tabla menú lado a lado
    const tblBase = { styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [9, 53, 121], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [240, 245, 255] } };

    (doc as any).autoTable({ ...tblBase,
      startY: 19, margin: { left: ML }, tableWidth: 80,
      head: [['Seccional', 'N°', '%']],
      body: secArr.map(([s, c]) => [s, c, pct(c)]),
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } },
    });
    (doc as any).autoTable({ ...tblBase,
      startY: 19, margin: { left: ML + 86 }, tableWidth: 80,
      head: [['Categoría (Menú)', 'N°', '%']],
      body: menuArr.map(([m, c]) => [m, c, pct(c)]),
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } },
    });
    (doc as any).autoTable({ ...tblBase,
      startY: 19, margin: { left: ML + 172 }, tableWidth: 113,
      head: [['Área', 'Asignados', '%']],
      body: respArr.map(([r, c]) => [r, c, pct(c)]),
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } },
    });

    this._pdfFtr(doc, fecha, W, H);

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // PÁGINA 10 — SUBMENÚS DETALLADOS
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    doc.addPage(); pg++;
    this._pdfHdr(doc, 'Detalle SubMenús', pg, W);
    doc.setFontSize(9); doc.setFontStyle('bold');
    doc.text('Distribución por SubMenús', ML, 17);
    doc.setFontStyle('normal');

    (doc as any).autoTable({ ...tblBase,
      startY: 19, margin: { left: ML }, tableWidth: 90,
      head: [['SubMenú 1', 'N°', '%']],
      body: sub1Arr.map(([s, c]) => [s, c, pct(c)]),
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } },
    });
    (doc as any).autoTable({ ...tblBase,
      startY: 19, margin: { left: ML + 96 }, tableWidth: 90,
      head: [['SubMenú 2', 'N°', '%']],
      body: sub2Arr.map(([s, c]) => [s, c, pct(c)]),
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } },
    });
    (doc as any).autoTable({ ...tblBase,
      startY: 19, margin: { left: ML + 192 }, tableWidth: 93,
      head: [['SubMenú 3', 'N°', '%']],
      body: sub3Arr.map(([s, c]) => [s, c, pct(c)]),
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } },
    });

    this._pdfFtr(doc, fecha, W, H);

    // ── Guardar ─────────────────────────────────────────────────────
    const nomArch = `Escalamientos_SAC_${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}.pdf`;
    doc.save(nomArch);
  }

  // ─── Navegación ──────────────────────────────────────────────────────────────
  verTicket(idTicket: string, idBeneficiario?: any): void {
    this.global.Id_Ticket = idTicket;
    localStorage.setItem('Id_Ticket', idTicket);
    if (idBeneficiario != null) {
      this.global.informacion = [[idBeneficiario]];
      localStorage.setItem('informacion', JSON.stringify([[idBeneficiario]]));
    }
    this.ruta.navigateByUrl('/ver-ticket');
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  badgeEstado(estado: string): string {
    switch (estado) {
      case 'Pendiente':  return 'badge badge-warning';
      case 'En Proceso': return 'badge badge-info';
      case 'Resuelto':   return 'badge badge-success';
      case 'Cerrado':    return 'badge badge-secondary';
      default:           return 'badge badge-light';
    }
  }

  onPageSizeChange(): void {
    this.paginaActual = 1;
    this.cdr.markForCheck();
  }

  // Parsea la parte YYYY-MM-DD de un string de fecha SIN conversión UTC,
  // evitando que la zona horaria del dispositivo desplace el día mostrado.
  private parseFechaLocal(fecha: any): Date | null {
    if (!fecha) return null;
    const m = String(fecha).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3]);
  }

  formatFecha(fecha: any): string {
    if (!fecha) return '-';
    const d = this.parseFechaLocal(fecha);
    if (!d) return '-';
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

}
